import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  SafeAreaView,
} from '../utils/webComponents';
import { useNavigation, useRoute } from '../utils/navigation';
import { LibraryBooks, Edit } from '@mui/icons-material';
import { useFlashcards } from '../context/FlashcardContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';

const EditSetScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { setId } = route.params || {};
  const { flashcardSets, studiedSets, updateFlashcardSet } = useFlashcards();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [setName, setSetName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  // Find the set (check both active and studied sets)
  const activeSet = flashcardSets.find(set => set.id === setId);
  const studiedSet = (studiedSets || []).find(set => set.id === setId);
  const setItem = activeSet || studiedSet;

  // Load set data
  useEffect(() => {
    if (setItem) {
      setSetName(setItem.name || '');
      setDescription(setItem.description || '');
    }
  }, [setItem]);

  // If set not found, navigate back
  useEffect(() => {
    if (!setItem && flashcardSets.length > 0 && studiedSets.length > 0) {
      setTimeout(() => {
        navigation.goBack();
      }, 2000);
    }
  }, [setItem, flashcardSets, studiedSets, navigation]);

  const handleUpdateSet = async () => {
    if (!setName.trim()) {
      return;
    }

    if (!setId) {
      return;
    }

    setLoading(true);

    try {
      updateFlashcardSet({
        id: setId,
        name: setName.trim(),
        description: description.trim(),
      });

      navigation.goBack();
    } catch (error) {
      console.error('Error updating set:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!setItem) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={styles.loadingContainer}>
          <Text style={[styles.loadingText, { color: theme.text }]}>
            {t('common.loading')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.contentContainer}>
          <View style={[styles.content, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <View style={styles.iconContainer}>
              <View style={[styles.iconCircle, { backgroundColor: theme.primaryLight }]}>
                <LibraryBooks sx={{ fontSize: 48, color: theme.primary }} />
              </View>
            </View>
            
            <Text style={[styles.title, { color: theme.text }]}>{t('set.editSet')}</Text>

            <View style={styles.form}>
              <View style={styles.inputContainer}>
                <Text style={[styles.inputLabel, { color: theme.text, marginBottom: 12 }]}>{t('createSet.setName')}</Text>
                <TextInput
                  style={[styles.textInput, { backgroundColor: theme.inputBackground || theme.surface, color: theme.text, borderColor: theme.border }]}
                  value={setName}
                  onChangeText={setSetName}
                  placeholder={t('createSet.setNamePlaceholder')}
                  placeholderTextColor={theme.textSecondary}
                />
              </View>

              <View style={styles.inputContainer}>
                <Text style={[styles.inputLabel, { color: theme.text, marginBottom: 12 }]}>{t('createSet.description')}</Text>
                <TextInput
                  style={[styles.textInput, styles.textArea, { backgroundColor: theme.inputBackground || theme.surface, color: theme.text, borderColor: theme.border }]}
                  value={description}
                  onChangeText={setDescription}
                  placeholder={t('createSet.descriptionPlaceholder')}
                  placeholderTextColor={theme.textSecondary}
                  multiline
                  numberOfLines={4}
                />
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={() => navigation.goBack()}
              >
                <Text style={[styles.cancelButtonText, { color: theme.text }]}>{t('createSet.cancel')}</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.button, styles.saveButton, { backgroundColor: theme.primary }, (loading || !setName.trim()) && styles.disabledButton]}
                onPress={handleUpdateSet}
                disabled={loading || !setName.trim()}
              >
                <Edit sx={{ fontSize: 20, color: 'white' }} />
                <Text style={[styles.saveButtonText, { color: 'white' }]}>
                  {loading ? t('common.loading') : t('common.save')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    fontSize: 18,
  },
  scrollView: {
    flex: 1,
    display: 'flex',
  },
  scrollContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    minHeight: '100vh',
  },
  contentContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 600,
  },
  content: {
    width: '100%',
    maxWidth: 600,
    padding: 32,
    borderRadius: 16,
    borderWidth: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    margin: '0 auto',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 32,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 32,
  },
  form: {
    marginBottom: 32,
  },
  inputContainer: {
    marginBottom: 24,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  textInput: {
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    borderWidth: 1,
    width: '100%',
    transition: 'border-color 0.2s',
  },
  textArea: {
    minHeight: 100,
    resize: 'vertical',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s, opacity 0.2s',
    cursor: 'pointer',
    gap: 8,
  },
  cancelButton: {
    borderWidth: 1,
  },
  saveButton: {
    borderWidth: 0,
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default EditSetScreen;
